/**
 * Hook para manejar la selección de permisos
 * Sistema Klever Octopus - Betel Natural
 */
import { useState, useCallback, useMemo } from 'react';

/**
 * Hook para gestionar la selección de permisos en formularios de roles
 * @param {Array} initialPermissions - Array de IDs de permisos seleccionados inicialmente
 * @param {Array} allPermissions - Array con todos los permisos disponibles
 * @returns {Object} Objeto con métodos y estado para manejar la selección
 */
export const usePermissionSelection = (initialPermissions = [], allPermissions = []) => {
  const [selectedPermissions, setSelectedPermissions] = useState(initialPermissions);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');

  // Obtener módulos únicos de los permisos
  const modules = useMemo(() => {
    const moduleSet = new Set(allPermissions.map(p => p.module));
    return ['all', ...Array.from(moduleSet)];
  }, [allPermissions]);

  // Filtrar permisos según búsqueda y módulo seleccionado
  const filteredPermissions = useMemo(() => {
    let filtered = allPermissions;

    // Filtrar por módulo
    if (selectedModule !== 'all') {
      filtered = filtered.filter(p => p.module === selectedModule);
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(lowerSearch) ||
        p.display_name?.toLowerCase().includes(lowerSearch) ||
        p.description?.toLowerCase().includes(lowerSearch)
      );
    }

    return filtered;
  }, [allPermissions, selectedModule, searchTerm]);

  // Agrupar permisos por grupo/categoría
  const groupedPermissions = useMemo(() => {
    const groups = {};
    
    filteredPermissions.forEach(permission => {
      const group = permission.group || 'other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(permission);
    });

    return groups;
  }, [filteredPermissions]);

  // Verificar si un permiso está seleccionado
  const isPermissionSelected = useCallback((permissionId) => {
    return selectedPermissions.includes(permissionId);
  }, [selectedPermissions]);

  // Alternar selección de un permiso
  const togglePermission = useCallback((permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  }, []);

  // Seleccionar todos los permisos filtrados
  const selectAll = useCallback(() => {
    const allIds = filteredPermissions.map(p => p.id);
    setSelectedPermissions(prev => {
      const newSelection = [...prev];
      allIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  }, [filteredPermissions]);

  // Deseleccionar todos los permisos filtrados
  const deselectAll = useCallback(() => {
    const allIds = filteredPermissions.map(p => p.id);
    setSelectedPermissions(prev => prev.filter(id => !allIds.includes(id)));
  }, [filteredPermissions]);

  // Seleccionar todos los permisos de un grupo
  const selectGroup = useCallback((group) => {
    const groupPermissions = allPermissions
      .filter(p => p.group === group)
      .map(p => p.id);
    
    setSelectedPermissions(prev => {
      const newSelection = [...prev];
      groupPermissions.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  }, [allPermissions]);

  // Deseleccionar todos los permisos de un grupo
  const deselectGroup = useCallback((group) => {
    const groupPermissions = allPermissions
      .filter(p => p.group === group)
      .map(p => p.id);
    
    setSelectedPermissions(prev => prev.filter(id => !groupPermissions.includes(id)));
  }, [allPermissions]);

  // Verificar si todos los permisos de un grupo están seleccionados
  const isGroupSelected = useCallback((group) => {
    const groupPermissions = allPermissions
      .filter(p => p.group === group)
      .map(p => p.id);
    
    return groupPermissions.length > 0 && 
           groupPermissions.every(id => selectedPermissions.includes(id));
  }, [allPermissions, selectedPermissions]);

  // Verificar si algunos (pero no todos) los permisos de un grupo están seleccionados
  const isGroupPartiallySelected = useCallback((group) => {
    const groupPermissions = allPermissions
      .filter(p => p.group === group)
      .map(p => p.id);
    
    const selectedCount = groupPermissions.filter(id => selectedPermissions.includes(id)).length;
    return selectedCount > 0 && selectedCount < groupPermissions.length;
  }, [allPermissions, selectedPermissions]);

  // Limpiar todas las selecciones
  const clearSelection = useCallback(() => {
    setSelectedPermissions([]);
  }, []);

  // Establecer permisos seleccionados
  const setPermissions = useCallback((permissions) => {
    setSelectedPermissions(permissions);
  }, []);

  // Resetear al estado inicial
  const reset = useCallback(() => {
    setSelectedPermissions(initialPermissions);
    setSearchTerm('');
    setSelectedModule('all');
  }, [initialPermissions]);

  // Estadísticas
  const stats = useMemo(() => ({
    total: allPermissions.length,
    selected: selectedPermissions.length,
    filtered: filteredPermissions.length,
    percentage: allPermissions.length > 0 
      ? Math.round((selectedPermissions.length / allPermissions.length) * 100) 
      : 0
  }), [allPermissions.length, selectedPermissions.length, filteredPermissions.length]);

  return {
    // Estado
    selectedPermissions,
    searchTerm,
    selectedModule,
    modules,
    filteredPermissions,
    groupedPermissions,
    stats,

    // Métodos de selección
    isPermissionSelected,
    togglePermission,
    selectAll,
    deselectAll,
    selectGroup,
    deselectGroup,
    isGroupSelected,
    isGroupPartiallySelected,
    clearSelection,
    setPermissions,

    // Métodos de filtrado
    setSearchTerm,
    setSelectedModule,

    // Métodos generales
    reset
  };
};

export default usePermissionSelection;
